"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, LogOut, Save, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";

export default function ProfilePage() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateProfile({ name: name.trim(), phone: phone.trim() });
    setSaved(true);
    setIsEditing(false);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg">
        <div className="text-center">
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
            Anda belum login
          </p>
          <Link
            to="/login"
            className="text-gold hover:text-gold-light font-medium transition-colors"
          >
            Masuk sekarang
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg px-4 pt-20 pb-24">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
              <User className="w-6 h-6 text-gold" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
                Profil Saya
              </h1>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Kelola informasi akun Anda
              </p>
            </div>
          </div>

          {/* User Info Card */}
          <div
            className={cn(
              "rounded-[28px] p-8 mb-6",
              "bg-light-card dark:bg-dark-card",
              "border border-dark-text-secondary/5 dark:border-white/5",
              "shadow-sm",
            )}
          >
            <div className="space-y-5">
              {/* Role Badge */}
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gold" />
                <span
                  className={cn(
                    "text-xs font-semibold px-3 py-1 rounded-full",
                    user.role === "ADMIN"
                      ? "bg-gold/10 text-gold"
                      : "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
                  )}
                >
                  {user.role === "ADMIN" ? "Administrator" : "Customer"}
                </span>
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className={cn(
                      "w-full pl-11 pr-4 py-3 rounded-2xl",
                      "bg-light-bg dark:bg-dark-bg",
                      "border border-dark-text-secondary/10 dark:border-white/10",
                      "text-light-text-primary dark:text-dark-text-primary",
                      "opacity-60 cursor-not-allowed",
                    )}
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Nama
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setIsEditing(true); }}
                    className={cn(
                      "w-full pl-11 pr-4 py-3 rounded-2xl",
                      "bg-light-bg dark:bg-dark-bg",
                      "border border-dark-text-secondary/10 dark:border-white/10",
                      "text-light-text-primary dark:text-dark-text-primary",
                      "focus:outline-none focus:border-gold/40 focus:ring-1 focus:ring-gold/20",
                      "transition-colors duration-200",
                    )}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Nomor Telepon
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setIsEditing(true); }}
                    placeholder="0812-3456-7890"
                    className={cn(
                      "w-full pl-11 pr-4 py-3 rounded-2xl",
                      "bg-light-bg dark:bg-dark-bg",
                      "border border-dark-text-secondary/10 dark:border-white/10",
                      "text-light-text-primary dark:text-dark-text-primary",
                      "placeholder:text-dark-text-secondary/40",
                      "focus:outline-none focus:border-gold/40 focus:ring-1 focus:ring-gold/20",
                      "transition-colors duration-200",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={!isEditing}
              className={cn(
                "flex-1 py-3.5 rounded-2xl font-semibold text-sm",
                "flex items-center justify-center gap-2",
                "transition-all duration-300 active:scale-[0.98]",
                isEditing
                  ? "bg-gold text-black hover:bg-gold-light shadow-[0_8px_32px_rgba(117,53,14,0.2)]"
                  : "bg-dark-text-secondary/10 text-dark-text-secondary/40 cursor-not-allowed",
              )}
            >
              <Save className="w-4 h-4" />
              <span>{saved ? "Tersimpan!" : "Simpan Perubahan"}</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className={cn(
                "flex-1 py-3.5 rounded-2xl font-semibold text-sm",
                "flex items-center justify-center gap-2",
                "border border-red-200 dark:border-red-800",
                "text-red-600 dark:text-red-400",
                "hover:bg-red-50 dark:hover:bg-red-900/20",
                "transition-all duration-200 active:scale-[0.98]",
              )}
            >
              <LogOut className="w-4 h-4" />
              <span>Keluar</span>
            </button>
          </div>

          {/* Back to dashboard */}
          <p className="text-center text-sm text-light-text-secondary dark:text-dark-text-secondary mt-6">
            <Link
              to="/dashboard"
              className="text-gold hover:text-gold-light font-medium transition-colors"
            >
              ← Kembali ke Dashboard
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
